import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Email,
  ShoppingCart,
  AccountCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useShoppingCart } from "./ShoppingCartProvider";

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { clearCart } = useShoppingCart();

  // Clear the shopping cart when the payment success page loads
  useEffect(() => {
    clearCart();
  }, [clearCart]); // clearCart is now stable with useCallback

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      sx={{ px: 2 }}
    >
      <Card
        elevation={8}
        sx={{
          maxWidth: 600,
          width: "100%",
          borderRadius: 3,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {/* Success Icon */}
          <CheckCircle
            sx={{
              fontSize: 80,
              color: "success.main",
              mb: 2,
              filter: "drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))",
            }}
          />

          {/* Main Title */}
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(45deg, #4CAF50, #2E7D32)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 3,
            }}
          >
            Payment Successful!
          </Typography>

          {/* Status Chips */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Chip
              icon={<Email />}
              label="Receipt Sent"
              color="info"
              variant="outlined"
            />
            <Chip
              icon={<ShoppingCart />}
              label="Cart Cleared"
              color="success"
              variant="outlined"
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Key Information */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              What happens next?
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Email color="primary" />
                <Typography variant="body1" color="text.secondary">
                  Check your email for the receipt and order details
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccountCircle color="primary" />
                <Typography variant="body1" color="text.secondary">
                  Complete any required forms in your account
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Action Button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/account")}
            startIcon={<AccountCircle />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #2196F3, #1976D2)",
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2, #1565C0)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(33, 150, 243, 0.3)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Go to Account Page
          </Button>

          {/* Thank you message */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, fontStyle: "italic" }}
          >
            Thank you for choosing us! 🎉
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentSuccessPage;
